import React, { useEffect, useRef } from 'react';

interface FocusTrapProps {
  children: React.ReactNode;
  onEscape?: () => void;
  initialFocusRef?: React.RefObject<HTMLElement>;
}

function getFocusable(el: HTMLElement | null): HTMLElement[] {
  if (!el) return [];
  const selectors = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');
  const nodes = Array.from(el.querySelectorAll<HTMLElement>(selectors));
  return nodes.filter(n => !!(n.offsetWidth || n.offsetHeight || n.getClientRects().length));
}

const FocusTrap: React.FC<FocusTrapProps> = ({ children, onEscape, initialFocusRef }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const root = rootRef.current;
    const focusable = getFocusable(root);
    try {
      if (initialFocusRef && initialFocusRef.current) initialFocusRef.current.focus();
      else if (focusable.length) focusable[0].focus();
      else root?.focus();
    } catch { /* ignore */ }

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onEscape?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = getFocusable(root);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || active === root) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      try { previouslyFocused.current?.focus(); } catch {}
    };
  }, [onEscape, initialFocusRef]);

  return (
    <div ref={rootRef} tabIndex={-1}>
      {children}
    </div>
  );
};

export default FocusTrap;
